// src/pages/Clinic/ClinicProfilePage.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Avatar,
  IconButton,
  Chip,
  Stack,
  Grid,
  Divider,
  Switch,
  FormControlLabel,
  Skeleton,
  Fade,
  Zoom,
  Tooltip,
  Card,
  CardContent,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  PhotoCamera,
  Delete as DeleteIcon,
  Business as BusinessIcon,
  Language as LanguageIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Public as PublicIcon,
  Schedule as ScheduleIcon,
  AttachMoney as MoneyIcon,
  WhatsApp as WhatsAppIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  CloudUpload as CloudUploadIcon,
} from '@mui/icons-material';
import { toast } from 'react-hot-toast';
import axiosInstance from '../../axiosInstance';
import { useAuth } from '../../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';

const MotionPaper = motion(Paper);
const MotionCard = motion(Card);
const MotionBox = motion(Box);

const ClinicProfilePage = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const [clinic, setClinic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [removeLogo, setRemoveLogo] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [clinicId, setClinicId] = useState(null);

  useEffect(() => {
    fetchClinicData();
  }, []);

  const fetchClinicData = async () => {
    try {
      const { data: memberships } = await axiosInstance.get('/clinics/my-memberships/');
      const list = Array.isArray(memberships) ? memberships : [];
      if (!list.length) {
        toast.error('No perteneces a ninguna clínica');
        setLoading(false);
        return;
      }

      // Toma la membresía activa si existe, si no la primera
      const mActive = list.find(m => m.activo) || list[0];

      // ID de clínica compatible con ambas formas (anidado o “legacy”)
      const cId = mActive?.clinica?.id ?? mActive?.clinica_id ?? null;
      if (!cId) {
        console.warn('No se pudo resolver el ID de la clínica desde memberships:', mActive);
        toast.error('No se pudo identificar la clínica');
        setLoading(false);
        return;
      }

      setClinicId(cId);
      setIsAdmin((mActive?.rol || '') === 'Administrador');

      // Carga del detalle de la clínica
      const { data: clinicData } = await axiosInstance.get(`/clinicas/${cId}/`);
      setClinic(clinicData);
      setForm({
        nombre: clinicData?.nombre || '',
        sobre: clinicData?.sobre || '',
        telefono: clinicData?.telefono || '',
        email_contacto: clinicData?.email_contacto || '',
        whatsapp: clinicData?.whatsapp || '',
        sitio_web: clinicData?.sitio_web || '',
        direccion: clinicData?.direccion || '',
        pais: clinicData?.pais || '',
        ciudad: clinicData?.ciudad || '',
        estado_provincia: clinicData?.estado_provincia || '',
        codigo_postal: clinicData?.codigo_postal || '',
        timezone: clinicData?.timezone || 'UTC',
        moneda: (clinicData?.moneda || 'USD').toUpperCase(),
        permitir_agendamiento_online: !!clinicData?.permitir_agendamiento_online,
        cuenta_paypal: clinicData?.cuenta_paypal || ''
      });

      setLogoPreview(constructImageUrl(clinicData?.logo));
    } catch (error) {
      console.error('Error fetching clinic:', error);
      toast.error('Error al cargar datos de la clínica');
    } finally {
      setLoading(false);
    }
  };


  const constructImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    const base = axiosInstance.defaults.baseURL || 'http://127.0.0.1:8000';
    return `${base}${path.startsWith('/') ? '' : '/'}${path}`;
  };

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const onLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Solo se permiten imágenes');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('La imagen no debe superar 5MB');
      return;
    }

    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
    setRemoveLogo(false);
  };

  const onRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
    setRemoveLogo(true);
  };

  const onSave = async () => {
    if (!isAdmin || !clinicId) return;
    if (!form.nombre?.trim()) {
      toast.error('El nombre de la clínica es requerido');
      return;
    }

    setSaving(true);
    try {
      const cleanData = { ...form };
      if (cleanData.sitio_web) {
        const v = cleanData.sitio_web.trim();
        if (v && !/^https?:\/\//i.test(v)) {
          cleanData.sitio_web = `https://${v}`;
        }
      }

      const hasFile = !!logoFile;
      const needsMultipart = hasFile || removeLogo;

      if (needsMultipart) {
        const fd = new FormData();
        Object.entries(cleanData).forEach(([k, v]) => {
          if (typeof v === 'boolean') {
            fd.append(k, v ? 'true' : 'false');
          } else if (v !== null && v !== undefined) {
            fd.append(k, v);
          }
        });

        if (hasFile) fd.append('logo', logoFile);
        if (removeLogo) fd.append('remove_logo', 'true');

        const { data: updated } = await axiosInstance.put(
          `/clinicas/${clinicId}/`,
          fd,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );

        setClinic(updated);
        setLogoFile(null);
        setRemoveLogo(false);
        setLogoPreview(constructImageUrl(updated.logo));
        setEditMode(false);
        toast.success('✨ Clínica actualizada exitosamente');
      } else {
        const { data: updated } = await axiosInstance.put(`/clinicas/${clinicId}/`, cleanData);
        setClinic(updated);
        setEditMode(false);
        toast.success('✨ Clínica actualizada exitosamente');
      }
    } catch (e) {
      console.error('Error:', e.response?.data || e);
      const errorMsg = e.response?.data?.sitio_web?.[0] || 'No se pudo actualizar la clínica';
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const onCancel = () => {
    setForm(clinic);
    setLogoFile(null);
    setLogoPreview(constructImageUrl(clinic?.logo));
    setRemoveLogo(false);
    setEditMode(false);
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 6 }}>
        <Stack spacing={4}>
          <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 4 }} />
          <Grid container spacing={3}>
            {[1, 2, 3, 4].map((i) => (
              <Grid item xs={12} md={6} key={i}>
                <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 3 }} />
              </Grid>
            ))}
          </Grid>
        </Stack>
      </Container>
    );
  }

  if (!clinic) {
    return (
      <Container maxWidth="lg" sx={{ py: 8, textAlign: 'center' }}>
        <BusinessIcon sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
        <Typography variant="h5" color="text.secondary">
          No se encontró información de la clínica
        </Typography>
      </Container>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(
          theme.palette.secondary.main,
          0.05
        )} 100%)`,
        py: 6,
      }}
    >
      <Container maxWidth="xl">
        <AnimatePresence mode="wait">
          {/* Header con Logo y Nombre */}
          <MotionPaper
            key="clinic-header"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            elevation={0}
            sx={{
              position: 'relative',
              overflow: 'hidden',
              borderRadius: 6,
              mb: 4,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              color: 'white',
              p: 6,
            }}
          >
            {/* Decorative circles */}
            <Box
              sx={{
                position: 'absolute',
                top: -50,
                right: -50,
                width: 200,
                height: 200,
                borderRadius: '50%',
                background: alpha('#fff', 0.1),
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                bottom: -30,
                left: -30,
                width: 150,
                height: 150,
                borderRadius: '50%',
                background: alpha('#fff', 0.08),
              }}
            />

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={4} alignItems="center" sx={{ position: 'relative', zIndex: 1 }}>
              {/* Logo */}
              <Box sx={{ position: 'relative' }}>
                <Avatar
                  src={logoPreview}
                  sx={{
                    width: 160,
                    height: 160,
                    border: `6px solid ${alpha('#fff', 0.3)}`,
                    boxShadow: `0 8px 32px ${alpha('#000', 0.3)}`,
                    transition: 'transform 0.3s ease',
                    '&:hover': {
                      transform: 'scale(1.05)',
                    },
                  }}
                >
                  <BusinessIcon sx={{ fontSize: 80 }} />
                </Avatar>

                {editMode && (
                  <Zoom in={editMode}>
                    <Box sx={{ position: 'absolute', bottom: 0, right: 0 }}>
                      <input
                        accept="image/*"
                        style={{ display: 'none' }}
                        id="logo-upload"
                        type="file"
                        onChange={onLogoChange}
                      />
                      <label htmlFor="logo-upload">
                        <Tooltip title="Cambiar logo">
                          <IconButton
                            component="span"
                            sx={{
                              bgcolor: 'white',
                              color: 'primary.main',
                              boxShadow: 3,
                              '&:hover': {
                                bgcolor: 'grey.100',
                                transform: 'rotate(15deg)',
                              },
                              transition: 'all 0.3s ease',
                            }}
                          >
                                                        <PhotoCamera />
                          </IconButton>
                        </Tooltip>
                      </label>
                      {logoPreview && (
                        <Tooltip title="Eliminar logo">
                          <IconButton
                            onClick={onRemoveLogo}
                            sx={{
                              bgcolor: 'error.main',
                              color: 'white',
                              ml: 1,
                              boxShadow: 3,
                              '&:hover': {
                                bgcolor: 'error.dark',
                                transform: 'rotate(-15deg)',
                              },
                              transition: 'all 0.3s ease',
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Zoom>
                )}
              </Box>

              {/* Clinic Info */}
              <Box sx={{ flex: 1, textAlign: { xs: 'center', md: 'left' } }}>
                {editMode ? (
                  <TextField
                    name="nombre"
                    value={form.nombre || ''}
                    onChange={onChange}
                    variant="outlined"
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontSize: '2rem',
                        fontWeight: 'bold',
                        color: 'white',
                        bgcolor: alpha('#fff', 0.15),
                        backdropFilter: 'blur(10px)',
                        borderRadius: 3,
                        '& fieldset': {
                          borderColor: alpha('#fff', 0.3),
                        },
                        '&:hover fieldset': {
                          borderColor: alpha('#fff', 0.5),
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'white',
                        },
                      },
                      '& .MuiInputBase-input': {
                        color: 'white',
                      },
                    }}
                  />
                ) : (
                  <Typography variant="h3" sx={{ fontWeight: 800, mb: 2, textShadow: '0 2px 10px rgba(0,0,0,0.2)' }}>
                    {clinic.nombre}
                  </Typography>
                )}

                <Stack direction="row" spacing={2} sx={{ mt: 2, justifyContent: { xs: 'center', md: 'flex-start' } }}>
                  <Chip
                    icon={<CheckCircleIcon />}
                    label={isAdmin ? 'Administrador' : 'Miembro'}
                    sx={{
                      bgcolor: alpha('#fff', 0.25),
                      backdropFilter: 'blur(10px)',
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '1rem',
                      height: 40,
                      px: 2,
                      border: `2px solid ${alpha('#fff', 0.3)}`,
                    }}
                  />
                  {clinic.permitir_agendamiento_online && (
                    <Chip
                      icon={<ScheduleIcon />}
                      label="Agendamiento Online"
                      sx={{
                        bgcolor: alpha('#4caf50', 0.3),
                        backdropFilter: 'blur(10px)',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '1rem',
                        height: 40,
                        px: 2,
                        border: `2px solid ${alpha('#fff', 0.3)}`,
                      }}
                    />
                  )}
                </Stack>
              </Box>

              {/* Action Buttons */}
              <Stack spacing={2}>
                {editMode ? (
                  <>
                    <Button
                      variant="contained"
                      size="large"
                      startIcon={saving ? null : <SaveIcon />}
                      onClick={onSave}
                      disabled={saving}
                      sx={{
                        bgcolor: 'white',
                        color: 'primary.main',
                        fontWeight: 'bold',
                        fontSize: '1.1rem',
                        py: 1.5,
                        px: 4,
                        borderRadius: 3,
                        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                        '&:hover': {
                          bgcolor: 'grey.100',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 6px 25px rgba(0,0,0,0.2)',
                        },
                        transition: 'all 0.3s ease',
                      }}
                    >
                      {saving ? 'Guardando...' : 'Guardar Cambios'}
                    </Button>
                    <Button
                      variant="outlined"
                      size="large"
                      startIcon={<CloseIcon />}
                      onClick={onCancel}
                      sx={{
                        borderColor: 'white',
                        color: 'white',
                        fontWeight: 'bold',
                        py: 1.5,
                        px: 4,
                        borderRadius: 3,
                        borderWidth: 2,
                        '&:hover': {
                          borderColor: 'white',
                          bgcolor: alpha('#fff', 0.1),
                          borderWidth: 2,
                        },
                      }}
                    >
                      Cancelar
                    </Button>
                  </>
                ) : (
                  isAdmin && (
                    <Button
                      variant="contained"
                      size="large"
                      startIcon={<EditIcon />}
                      onClick={() => setEditMode(true)}
                      sx={{
                        bgcolor: 'white',
                        color: 'primary.main',
                        fontWeight: 'bold',
                        fontSize: '1.1rem',
                        py: 1.5,
                        px: 4,
                        borderRadius: 3,
                        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                        '&:hover': {
                          bgcolor: 'grey.100',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 6px 25px rgba(0,0,0,0.2)',
                        },
                        transition: 'all 0.3s ease',
                      }}
                    >
                      Editar Perfil
                    </Button>
                  )
                )}
              </Stack>
            </Stack>
          </MotionPaper>

          {/* Main Content Grid */}
          <Grid key="clinic-content" container spacing={4}>
            {/* About Section */}
            <Grid item xs={12} lg={8}>
              <MotionCard
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                elevation={0}
                sx={{
                  borderRadius: 4,
                  p: 4,
                  height: '100%',
                  background: 'white',
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: `0 8px 30px ${alpha(theme.palette.primary.main, 0.12)}`,
                    transform: 'translateY(-4px)',
                  },
                }}
              >
                <Stack direction="row" alignItems="center" spacing={2} mb={3}>
                  <Box
                    sx={{
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      borderRadius: 3,
                      p: 1.5,
                      display: 'flex',
                    }}
                  >
                    <InfoIcon sx={{ fontSize: 32, color: 'primary.main' }} />
                  </Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary' }}>
                    Acerca de la Clínica
                  </Typography>
                </Stack>

                {editMode ? (
                  <TextField
                    name="sobre"
                    value={form.sobre || ''}
                    onChange={onChange}
                    multiline
                    rows={6}
                    fullWidth
                    placeholder="Describe tu clínica, servicios y valores..."
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        fontSize: '1.1rem',
                      },
                    }}
                  />
                ) : (
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: '1.1rem',
                      lineHeight: 1.8,
                      color: 'text.secondary',
                    }}
                  >
                    {clinic.sobre || 'No hay descripción disponible.'}
                  </Typography>
                )}
              </MotionCard>
            </Grid>

            {/* Quick Info Cards */}
            <Grid item xs={12} lg={4}>
              <Stack spacing={3}>
                {/* Timezone Card */}
                <InfoCard
                  icon={<ScheduleIcon />}
                  title="Zona Horaria"
                  value={editMode ? (
                    <TextField
                      select
                      name="timezone"
                      value={form.timezone || 'UTC'}
                      onChange={onChange}
                      fullWidth
                      SelectProps={{ native: true }}
                      size="small"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    >
                      <option value="UTC">UTC</option>
                      <option value="America/Tegucigalpa">America/Tegucigalpa</option>
                      <option value="America/Mexico_City">America/Mexico_City</option>
                      <option value="America/Bogota">America/Bogota</option>
                      <option value="America/Lima">America/Lima</option>
                      <option value="America/Santiago">America/Santiago</option>
                      <option value="Europe/Madrid">Europe/Madrid</option>
                    </TextField>
                  ) : clinic.timezone}
                  color="primary"
                  delay={0.2}
                />

                {/* Currency Card */}
                <InfoCard
                  icon={<MoneyIcon />}
                  title="Moneda"
                  value={editMode ? (
                    <TextField
                      select
                      name="moneda"
                      value={form.moneda || 'USD'}
                      onChange={onChange}
                      fullWidth
                      SelectProps={{ native: true }}
                      size="small"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    >
                      <option value="USD">USD</option>
                      <option value="HNL">HNL</option>
                      <option value="MXN">MXN</option>
                      <option value="EUR">EUR</option>
                      <option value="COP">COP</option>
                      <option value="PEN">PEN</option>
                      <option value="CLP">CLP</option>
                    </TextField>
                  ) : (clinic.moneda || 'USD').toUpperCase()}
                  color="success"
                  delay={0.3}
                />

                {/* Online Booking Card */}
                <InfoCard
                  icon={<CheckCircleIcon />}
                  title="Agendamiento Online"
                  value={editMode ? (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={!!form.permitir_agendamiento_online}
                          onChange={(e) => onChange({ target: { name: 'permitir_agendamiento_online', value: e.target.checked, type: 'checkbox' } })}
                          color="success"
                        />
                      }
                      label={form.permitir_agendamiento_online ? 'Habilitado' : 'Deshabilitado'}
                    />
                  ) : clinic.permitir_agendamiento_online ? 'Habilitado' : 'Deshabilitado'}
                  color={clinic.permitir_agendamiento_online ? 'success' : 'warning'}
                  delay={0.4}
                />
              </Stack>
            </Grid>

            {/* Contact Information */}
            <Grid item xs={12} md={6}>
              <ContactCard
                icon={<PhoneIcon />}
                title="Información de Contacto"
                fields={[
                  {
                    name: 'telefono',
                    label: 'Teléfono',
                    icon: <PhoneIcon />,
                    placeholder: '+504 1234-5678',
                  },
                  {
                    name: 'email_contacto',
                    label: 'Email',
                    icon: <EmailIcon />,
                    placeholder: 'contacto@clinica.com',
                    type: 'email',
                  },
                  {
                    name: 'whatsapp',
                    label: 'WhatsApp',
                    icon: <WhatsAppIcon />,
                    placeholder: '+504 1234-5678',
                  },
                  {
                    name: 'sitio_web',
                    label: 'Sitio Web',
                    icon: <LanguageIcon />,
                    placeholder: 'www.clinica.com',
                  },
                ]}
                form={form}
                clinic={clinic}
                editMode={editMode}
                onChange={onChange}
                delay={0.5}
              />
            </Grid>

            {/* Location Information */}
            <Grid item xs={12} md={6}>
              <ContactCard
                icon={<LocationIcon />}
                title="Ubicación"
                fields={[
                  {
                    name: 'direccion',
                    label: 'Dirección',
                    icon: <LocationIcon />,
                    placeholder: 'Calle Principal #123',
                    multiline: true,
                  },
                  {
                    name: 'ciudad',
                    label: 'Ciudad',
                    icon: <PublicIcon />,
                    placeholder: 'Tegucigalpa',
                  },
                  {
                    name: 'estado_provincia',
                    label: 'Estado/Provincia',
                    icon: <PublicIcon />,
                    placeholder: 'Francisco Morazán',
                  },
                  {
                    name: 'pais',
                    label: 'País',
                    icon: <PublicIcon />,
                    placeholder: 'Honduras',
                  },
                  {
                    name: 'codigo_postal',
                    label: 'Código Postal',
                    icon: <LocationIcon />,
                    placeholder: '11101',
                  },
                ]}
                form={form}
                clinic={clinic}
                editMode={editMode}
                onChange={onChange}
                delay={0.6}
              />
            </Grid>

            {/* Payment Information */}
            {(isAdmin || clinic.cuenta_paypal) && (
              <Grid item xs={12}>
                <MotionCard
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.7 }}
                  elevation={0}
                  sx={{
                    borderRadius: 4,
                    p: 4,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.05)} 0%, ${alpha(
                      theme.palette.info.light,
                      0.1
                    )} 100%)`,
                    border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                    boxShadow: `0 4px 20px ${alpha(theme.palette.info.main, 0.1)}`,
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={2} mb={3}>
                    <Box
                      sx={{
                        bgcolor: alpha(theme.palette.info.main, 0.15),
                        borderRadius: 3,
                        p: 1.5,
                        display: 'flex',
                      }}
                    >
                      <MoneyIcon sx={{ fontSize: 32, color: 'info.main' }} />
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary' }}>
                      Información de Pagos
                    </Typography>
                  </Stack>

                  {editMode ? (
                    <TextField
                      name="cuenta_paypal"
                      value={form.cuenta_paypal || ''}
                      onChange={onChange}
                      fullWidth
                      label="Cuenta PayPal"
                      placeholder="paypal@clinica.com"
                      type="email"
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 3,
                          bgcolor: 'white',
                          fontSize: '1.1rem',
                        },
                      }}
                    />
                  ) : (
                    <Typography variant="h6" sx={{ color: 'info.main', fontWeight: 600 }}>
                      {clinic.cuenta_paypal || 'No configurada'}
                    </Typography>
                  )}
                </MotionCard>
              </Grid>
            )}
          </Grid>
        </AnimatePresence>
      </Container>
    </Box>
  );
};

  // Helper Component: Info Card
  const InfoCard = ({ icon, title, value, color = 'primary', delay = 0 }) => {
    const theme = useTheme();
    
    return (
      <MotionCard
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4, delay }}
        elevation={0}
        sx={{
          borderRadius: 3,
          p: 3,
          background: `linear-gradient(135deg, ${alpha(theme.palette[color].main, 0.05)} 0%, ${alpha(
            theme.palette[color].light,
            0.1
          )} 100%)`,
          border: `2px solid ${alpha(theme.palette[color].main, 0.2)}`,
          boxShadow: `0 4px 15px ${alpha(theme.palette[color].main, 0.08)}`,
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: `0 8px 25px ${alpha(theme.palette[color].main, 0.15)}`,
            transform: 'translateY(-4px)',
            borderColor: alpha(theme.palette[color].main, 0.4),
          },
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2} mb={1.5}>
          <Box
            sx={{
              bgcolor: alpha(theme.palette[color].main, 0.15),
              borderRadius: 2,
              p: 1,
              display: 'flex',
            }}
          >
            {React.cloneElement(icon, { sx: { fontSize: 24, color: `${color}.main` } })}
          </Box>
          <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.85rem' }}>
            {title}
          </Typography>
        </Stack>
        <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', ml: 1 }}>
          {value}
        </Typography>
      </MotionCard>
    );
  };

  // Helper Component: Contact Card
  const ContactCard = ({ icon, title, fields, form, clinic, editMode, onChange, delay = 0 }) => {
    const theme = useTheme();

    return (
      <MotionCard
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay }}
        elevation={0}
        sx={{
          borderRadius: 4,
          p: 4,
          height: '100%',
          background: 'white',
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: `0 8px 30px ${alpha(theme.palette.primary.main, 0.12)}`,
            transform: 'translateY(-4px)',
          },
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2} mb={4}>
          <Box
            sx={{
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              borderRadius: 3,
              p: 1.5,
              display: 'flex',
            }}
          >
            {React.cloneElement(icon, { sx: { fontSize: 32, color: 'primary.main' } })}
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary' }}>
            {title}
          </Typography>
        </Stack>

        <Stack spacing={3}>
          {fields.map((field) => (
            <Box key={field.name}>
              {editMode ? (
                <TextField
                  name={field.name}
                  value={form[field.name] || ''}
                  onChange={onChange}
                  fullWidth
                  label={field.label}
                  placeholder={field.placeholder}
                  type={field.type || 'text'}
                  multiline={field.multiline}
                  rows={field.multiline ? 3 : 1}
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <Box sx={{ mr: 1, display: 'flex', color: 'text.secondary' }}>
                        {field.icon}
                      </Box>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                                          borderRadius: 3,
                      fontSize: '1.05rem',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.02),
                      },
                      '&.Mui-focused': {
                        bgcolor: alpha(theme.palette.primary.main, 0.03),
                      },
                    },
                  }}
                />
              ) : (
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box
                    sx={{
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                      borderRadius: 2,
                      p: 1,
                      display: 'flex',
                    }}
                  >
                    {React.cloneElement(field.icon, { sx: { fontSize: 20, color: 'primary.main' } })}
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.75rem' }}>
                      {field.label}
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary', fontSize: '1.05rem' }}>
                      {clinic[field.name] || '—'}
                    </Typography>
                  </Box>
                </Stack>
              )}
            </Box>
          ))}
        </Stack>
      </MotionCard>
    );
  };

export default ClinicProfilePage;


                
